import { useMutation } from '@tanstack/react-query';
import { Loader2, Store } from 'lucide-react';
import { useState } from 'react';
import { toast } from '@/components/ui/sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { tenantControllerUploadTenantIcon } from '@/generated/korza-api-client';
import { useIsMobile } from '@/hooks/use-mobile';
import { useI18nContext } from '@/i18n/i18n-react';
import { mobileLayer } from '@/lib/mobile-layer';
import { cn } from '@/lib/utils';
import { LogoFramingCard } from '@/pages/onboarding/logo-framing-card';

export interface AppLogoCardProps {
  tenantSlug: string;
  iconUrl: string | null;
  /** The new icon, framed and saved (a local URL until the page reloads). */
  onSaved: (iconUrl: string) => void;
}

/**
 * "Logo do app" at the top of the hub while the app is under construction:
 * the current icon (often the placeholder "Preencher depois" left on the
 * onboarding) and "Trocar logo", which frames a new one as the onboarding
 * does, in a sheet below 768px and a dialog from 768px up.
 */
export function AppLogoCard({
  tenantSlug,
  iconUrl,
  onSaved,
}: AppLogoCardProps) {
  const { LL } = useI18nContext();
  const [open, setOpen] = useState(false);

  return (
    <div className="flex flex-wrap items-center gap-3.5 rounded-[18px] bg-white p-3.5 text-[#15191E] shadow-[0_1px_2px_rgba(15,23,42,0.04),0_0_0_1px_rgba(21,25,30,0.07)] md:gap-5 md:rounded-[22px] md:p-5">
      {iconUrl ? (
        <img
          src={iconUrl}
          alt=""
          className="size-14 shrink-0 rounded-[14px] object-cover shadow-[0_0_0_1px_rgba(21,25,30,0.06)]"
        />
      ) : (
        <span className="flex size-14 shrink-0 items-center justify-center rounded-[14px] bg-[#F2F2F4]">
          <Store aria-hidden strokeWidth={1.5} className="size-5" />
        </span>
      )}
      <div className="flex min-w-0 flex-1 basis-48 flex-col gap-1">
        <h2 className="text-[15px] font-semibold tracking-[-0.01em]">
          {LL.appHub.logo.title()}
        </h2>
        <p className="text-[13px] leading-normal text-[#4B5361]">
          {LL.appHub.logo.hint()}
        </p>
      </div>
      <Button
        type="button"
        variant="outline"
        // Below md on a line of its own, so the hint keeps its width.
        className={cn('shrink-0 max-md:w-full', mobileLayer.touch)}
        onClick={() => setOpen(true)}
      >
        {LL.appHub.logo.change()}
      </Button>
      {open && (
        <AppLogoSheet
          tenantSlug={tenantSlug}
          onOpenChange={setOpen}
          onSaved={(url) => {
            onSaved(url);
            setOpen(false);
          }}
        />
      )}
    </div>
  );
}

function AppLogoSheet({
  tenantSlug,
  onOpenChange,
  onSaved,
}: {
  tenantSlug: string;
  onOpenChange: (open: boolean) => void;
  onSaved: (iconUrl: string) => void;
}) {
  const { LL } = useI18nContext();
  const isMobile = useIsMobile();
  const [file, setFile] = useState<File | null>(null);
  const upload = useMutation({
    mutationFn: async (icon: File) => {
      const result = await tenantControllerUploadTenantIcon({
        path: { tenantSlug },
        body: { icon },
      });
      if (result.error !== undefined) throw new Error('Icon upload failed');
      return icon;
    },
    onSuccess: (icon) => {
      toast.success(LL.appHub.logo.saved());
      onSaved(URL.createObjectURL(icon));
    },
    onError: () => toast.error(LL.appHub.logo.saveError()),
  });

  const title = LL.appHub.logo.dialogTitle();
  const description = LL.appHub.logo.dialogDescription();
  const framing = (
    <LogoFramingCard onChange={setFile} disabled={upload.isPending} />
  );
  const save = (
    <Button
      type="button"
      className={mobileLayer.touch}
      disabled={!file || upload.isPending}
      onClick={() => file && upload.mutate(file)}
    >
      {upload.isPending ? (
        <Loader2 aria-hidden className="animate-spin" />
      ) : (
        LL.appHub.logo.save()
      )}
    </Button>
  );

  if (isMobile) {
    return (
      <Drawer open onOpenChange={onOpenChange}>
        <DrawerContent>
          <DrawerHeader className="text-left">
            <DrawerTitle>{title}</DrawerTitle>
            <DrawerDescription>{description}</DrawerDescription>
          </DrawerHeader>
          <div className="overflow-y-auto px-4">{framing}</div>
          <DrawerFooter>{save}</DrawerFooter>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {framing}
        <DialogFooter>{save}</DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
